import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputVideos from '@components/InputVideos';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { UPDATE_BRAND } from '@gql/brand';
import { toUploadFileInput, toUploadFilesInput } from '@modules/brands/utils';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Stack } from '@mui/material';

type UploadAsset = {
    _id?: string;
    url?: string;
    name?: string;
    size?: number;
    mimetype?: string;
};

type BrandFormValues = {
    brandVideos: Array<File | UploadAsset>;
};

type BrandVideosCardProps = {
    brand: {
        _id: string;
        brandName?: string;
        description?: string;
        sector?: { _id?: string };
        brandLogo?: UploadAsset;
        brandBanner?: UploadAsset;
        brandPhotos?: Array<UploadAsset>;
        brandVideos?: Array<UploadAsset>;
    };
    disabled?: boolean;
};

const BrandVideosCard = ({ brand, disabled }: BrandVideosCardProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const defaultValues = useMemo<BrandFormValues>(
        () => ({
            brandVideos: (brand?.brandVideos as Array<File | UploadAsset>) ?? [],
        }),
        [brand],
    );

    const { control, handleSubmit, reset } = useForm<BrandFormValues>({
        mode: 'onChange',
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const [updateBrand, { loading }] = useMutation(UPDATE_BRAND, {
        onCompleted: data => {
            enqueueSnackbar('Vidéos de la marque modifiées avec succès', {
                variant: 'success',
            });
            setFormState('view');
        },
        onError: error => {
            enqueueSnackbar(error.message, {
                variant: 'error',
            });
        },
    });

    const submitHandler = async (values: BrandFormValues) => {
        if (!brand?._id) return;

        await updateBrand({
            variables: {
                id: brand._id,
                brandInput: {
                    brandName: brand?.brandName,
                    description: brand?.description,
                    sector: brand?.sector?._id,
                    brandLogo: toUploadFileInput(brand?.brandLogo ?? null),
                    brandBanner: toUploadFileInput(brand?.brandBanner ?? null),
                    brandPhotos: toUploadFilesInput(brand?.brandPhotos ?? []),
                    brandVideos: toUploadFilesInput(values.brandVideos),
                },
            },
            context: {
                fetchOptions: {
                    useUpload: true,
                },
            },
        });

        reset(values);
    };

    return (
        <FormContainer
            onBack={() => { }}
            formTitle="Vidéos"
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            hideEdit={disabled}
            onCancel={() => {
                reset(defaultValues);
            }}
            breadcrumbs={['Marque', 'Vidéos']}
            onSubmit={handleSubmit(submitHandler)}
        >
            <InputVideos
                name="brandVideos"
                label="Vidéos de la marque"
                control={control}
                viewOnly={formState === 'view'}
                helpText="Formats acceptés: MP4, WebM, MOV (Max: 50MB par vidéo, 5 vidéos max)"
                validation={{
                    maxSizeInMB: 50,
                    maxDurationSeconds: 300,
                    maxCount: 5,
                    acceptedFormats: ['mp4', 'webm', 'mov'],
                }}
                existingFiles={brand?.brandVideos}
            />
        </FormContainer>
    );
};

export default BrandVideosCard;
